import { useScrollRevealChildren } from "../../../hooks/useScrollReveal";

export interface ProcessStep {
  step: string;
  title: string;
  description: string;
  icon: string;
}

export interface ProcessContent {
  badge?: string;
  headline: string;
  headlineAccent?: string;
  subtext: string;
  steps: ProcessStep[];
}

interface ProcessSectionProps {
  content: ProcessContent;
}

export default function ProcessSection({ content }: ProcessSectionProps) {
  const { badge, headline, headlineAccent, subtext, steps } = content;
  const headerRef = useScrollRevealChildren();
  const stepsRef = useScrollRevealChildren();

  return (
    <section className="relative bg-[#111010] py-32 overflow-hidden">
      {/* Background image overlay — subtle */}
      <div
        className="absolute inset-0 bg-cover bg-center opacity-[0.07]"
        style={{
          backgroundImage:
            "url(https://public.readdy.ai/ai/img_res/3c544f2a49277a198f9503b1968413f9.jpg)",
        }}
      />
      <div className="absolute inset-0 bg-gradient-to-b from-[#111010]/60 via-transparent to-[#111010]/80" />

      {/* Decorative large step number watermark */}
      <div
        className="absolute -bottom-10 right-0 text-white/[0.02] text-[30vw] font-bold leading-none select-none"
        aria-hidden="true"
        style={{ fontFamily: "'Playfair Display', serif" }}
      >
        05
      </div>

      <div className="relative z-10 max-w-screen-xl mx-auto px-6 xl:px-10">

        {/* Header */}
        <div ref={headerRef} className="max-w-2xl mx-auto text-center mb-24">
          {badge && (
            <div className="reveal flex items-center justify-center gap-4 mb-7">
              <div className="h-px w-12 bg-gradient-to-r from-transparent to-[#C9A961]" />
              <span
                className="text-[#C9A961] text-[10px] tracking-[0.3em] uppercase font-medium"
                style={{ fontFamily: "'Inter', sans-serif" }}
              >
                {badge}
              </span>
              <div className="h-px w-12 bg-gradient-to-l from-transparent to-[#C9A961]" />
            </div>
          )}
          <h2
            className="reveal delay-100 text-4xl md:text-5xl xl:text-[58px] text-white leading-tight mb-5"
            style={{ fontFamily: "'Playfair Display', serif", fontWeight: 700 }}
          >
            {headline}
            {headlineAccent && (
              <em className="block text-[#C9A961]" style={{ fontStyle: "italic" }}>
                {headlineAccent}
              </em>
            )}
          </h2>
          <p
            className="reveal delay-200 text-white/40 text-base leading-relaxed"
            style={{ fontFamily: "'Inter', sans-serif", fontWeight: 300 }}
          >
            {subtext}
          </p>
        </div>

        {/* Steps */}
        <div ref={stepsRef} className="relative">
          {/* Connecting gold line — desktop only */}
          <div className="hidden lg:block absolute top-10 left-[10%] right-[10%] h-px bg-gradient-to-r from-transparent via-[#C9A961]/20 to-transparent" />

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-10">
            {steps.map((step, idx) => (
              <div
                key={step.step}
                className={`reveal delay-${idx * 100 + 100} group flex flex-col items-center text-center`}
              >
                {/* Step icon circle */}
                <div className="relative mb-8">
                  {/* Outer ring */}
                  <div className="absolute -inset-2 border border-[#C9A961]/10 group-hover:border-[#C9A961]/40 transition-colors duration-700 rounded-none" />
                  <div className="w-20 h-20 flex items-center justify-center bg-[#1A1A1A] border border-[#C9A961]/20 group-hover:border-[#C9A961]/60 transition-all duration-500">
                    <i className={`${step.icon} text-[#C9A961] text-2xl`} />
                  </div>
                  {/* Step number */}
                  <div
                    className="absolute -top-4 -right-4 text-[#C9A961]/25 text-3xl font-bold leading-none group-hover:text-[#C9A961]/50 transition-colors duration-400"
                    style={{ fontFamily: "'Playfair Display', serif" }}
                  >
                    {step.step}
                  </div>
                </div>

                <h3
                  className="text-white text-[15px] font-semibold mb-3 group-hover:text-[#C9A961] transition-colors duration-300 leading-snug"
                  style={{ fontFamily: "'Playfair Display', serif" }}
                >
                  {step.title}
                </h3>
                <div className="w-6 h-px bg-[#C9A961]/30 mb-3 group-hover:w-10 group-hover:bg-[#C9A961] transition-all duration-400" />
                <p
                  className="text-white/30 text-xs leading-relaxed"
                  style={{ fontFamily: "'Inter', sans-serif" }}
                >
                  {step.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
